/**
 * ACTIVITY-CORE (F4-A) — textos humanos del feed por entityType + action.
 *
 * Estructura pura (parse/grupos/offline) en `@space/shared`; aquí SOLO las
 * frases i18n. `entityLabel` intenta sacar un nombre humano del patch
 * (title/name del snapshot o del diff).
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@space/shared";

const ENTITY_NAMES: Record<string, MessageDescriptor> = {
  course: msg`course`,
  course_editor: msg`course editor`,
  course_organization: msg`course publication`,
  unit: msg`unit`,
  lesson: msg`lesson`,
  block: msg`block`,
  enrollment: msg`enrollment`,
  lesson_progress: msg`lesson progress`,
  org_settings: msg`academy settings`,
  campus: msg`campus`,
  room: msg`room`,
  teacher_profile: msg`teacher profile`,
  student_profile: msg`student record`,
  guardian_link: msg`guardian link`,
  video_watch_progress: msg`watch progress`,
  program: msg`program`,
  program_course: msg`program course`,
  course_module: msg`module`,
  course_settings: msg`course settings`,
  resource: msg`resource`,
  question: msg`question`,
  cohort: msg`cohort`,
  cohort_teacher: msg`cohort teacher`,
  cohort_enrollment: msg`cohort enrollment`,
  cohort_group: msg`group`,
  cohort_group_member: msg`group member`,
  cohort_module_setting: msg`module setting`,
  schedule_slot: msg`schedule slot`,
  class_session: msg`class session`,
  attendance_record: msg`attendance`,
  assignment: msg`assignment`,
  assignment_submission: msg`submission`,
  assignment_grade: msg`grade`,
  grade_category: msg`grade category`,
  grade_item: msg`grade item`,
  forum: msg`forum`,
  forum_thread: msg`forum thread`,
  forum_post: msg`forum post`,
  announcement: msg`announcement`,
  payment: msg`payment`,
  lead: msg`lead`,
  import_job: msg`import`,
};

const ACTION_TEXTS: Record<string, MessageDescriptor> = {
  created: msg`created`,
  updated: msg`updated`,
  deleted: msg`moved to trash`,
  restored: msg`restored`,
  reverted: msg`reverted`,
  purged: msg`permanently deleted`,
  grade_posted: msg`posted a grade for`,
  payment_verified: msg`verified a payment for`,
  payment_rejected: msg`rejected a payment for`,
  attendance_taken: msg`took attendance for`,
  enrollment_activated: msg`activated an enrollment for`,
  stage_changed: msg`moved the pipeline stage of`,
  grades_sealed: msg`sealed final grades for`,
  recording_attached: msg`attached a recording to`,
  imported: msg`imported data via`,
};

export function entityTypeText(entityType: string): MessageDescriptor {
  return ENTITY_NAMES[entityType] ?? msg`item`;
}

export function actionText(action: string): MessageDescriptor {
  return ACTION_TEXTS[action] ?? msg`changed`;
}

/** Nombre humano de la entidad, extraído del patch (new → old). */
export function entityLabel(row: Pick<ActivityRow, "patch">): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["title", "name", "code", "entity"]) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    for (const side of [change.new, change.old]) {
      if (typeof side === "string" && side.length > 0) {
        return side;
      }
    }
  }
  return null;
}

/** Ruta de la app a la que enlaza una entrada (null = sin deep-link). */
export function entityLink(
  row: Pick<ActivityRow, "entityType" | "entityId" | "patch">,
): string | null {
  const patch = parseActivityPatch(row.patch);
  const field = (key: string): string | null => {
    const c = patch[key];
    const v = c?.new ?? c?.old;
    return typeof v === "string" && v.length > 0 ? v : null;
  };
  switch (row.entityType) {
    case "cohort": {
      return `/academy/cohorts/${row.entityId}`;
    }
    case "cohort_enrollment":
    case "schedule_slot":
    case "class_session": {
      return field("cohort_id") ? `/academy/cohorts/${field("cohort_id")}` : null;
    }
    case "program": {
      return `/academy/programs/${row.entityId}`;
    }
    case "campus": {
      return `/academy/campuses/${row.entityId}`;
    }
    case "announcement": {
      return "/academy/announcements";
    }
    case "course": {
      return `/catalog/${row.entityId}`;
    }
    case "unit":
    case "lesson":
    case "block":
    case "course_module":
    case "resource": {
      return field("course_id") ? `/authoring/${field("course_id")}` : null;
    }
    default: {
      return null;
    }
  }
}
